import { useEffect } from 'react'
import { useLocale } from '@/shared/i18n/use-translation'

/**
 * Production site origin. Used to build canonical / og:url.
 * Override at build time with `VITE_SITE_URL`. Falls back to the
 * production domain so static crawls outside the build env still
 * see the right canonical.
 */
const SITE_URL = (
  import.meta.env.VITE_SITE_URL ?? 'https://abdallah-issa.com'
).replace(/\/$/, '')

/**
 * Default OG / Twitter image. Lives under `public/` so the bundler
 * doesn't fingerprint it; crawlers can fetch it at the same URL
 * across deploys.
 */
const DEFAULT_OG_IMAGE = '/og-image.png'

interface SeoProps {
  /**
   * Page <title>. The site name suffix is appended unless `noSuffix`
   * is set. Pages SHOULD pass an Arabic + English bilingual title via
   * the locale-aware caller; this component only sees the resolved
   * string.
   */
  title: string
  description?: string
  /** Page URL relative to the site origin, e.g. `/blog/my-post`. */
  path?: string
  /**
   * Hero / share image. Provide an absolute URL or a path under
   * `public/` (will be prefixed with the site origin).
   */
  image?: string
  /** OG type. Defaults to `website`; articles use `article`. */
  type?: 'website' | 'article' | 'profile' | 'product'
  /**
   * If true, the page should not be indexed (e.g. dashboard). Adds a
   * `noindex,follow` robots meta.
   */
  noIndex?: boolean
  /** Skip appending the brand suffix to the title. */
  noSuffix?: boolean
  /**
   * Optional JSON-LD payload (single object or array). Rendered into
   * a single <script type="application/ld+json"> tag managed by this
   * component.
   */
  jsonLd?: object | object[]
  /**
   * Article-specific Open Graph metadata. Only emitted when
   * `type === 'article'`. Facebook and LinkedIn use these to render
   * the publish date + author byline in shared cards.
   */
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
    section?: string
    tags?: string[]
  }
}

const SUFFIX = {
  ar: 'عبدالله عيسى',
  en: 'Abdallah Issa',
}

const DEFAULT_DESCRIPTION = {
  ar: 'كورسات، أدوات، وبرومبتات في الذكاء الاصطناعي، التصميم، والتطوير من عبدالله عيسى.',
  en: 'Courses, tools, and prompts for AI, design, and engineering — from Abdallah Issa.',
}

function fallbackDescription(path: string | undefined, title: string, locale: 'ar' | 'en'): string {
  if (!path || path === '/') return DEFAULT_DESCRIPTION[locale]
  const t = title.trim()
  if (path.startsWith('/blog/'))
    return locale === 'ar'
      ? `مقال: ${t} — تحليل عملي وتطبيق في الذكاء الاصطناعي والتصميم.`
      : `Article: ${t} — practical insights on AI and design.`
  if (path.startsWith('/prompt-engineering/'))
    return locale === 'ar'
      ? `دليل هندسة البرومبت: ${t} — شرح عملي مع أمثلة.`
      : `Prompt Engineering Guide: ${t} — practical guide with examples.`
  if (path.startsWith('/free-apis/'))
    return locale === 'ar'
      ? `${t} — دليل API مجاني للمطورين مع أمثلة استخدام.`
      : `${t} — free API guide for developers with examples.`
  if (path.startsWith('/toolshub/'))
    return locale === 'ar' ? `أداة ${t} — شرح الإمكانيات والاستخدام.` : `Tool: ${t} — capabilities and use cases.`
  if (path.startsWith('/prompts/'))
    return locale === 'ar' ? `برومبت: ${t} — جاهز للنسخ والاستخدام في أدوات AI.` : `Prompt: ${t} — ready to copy for AI tools.`
  if (path.startsWith('/course/'))
    return locale === 'ar' ? `دورة ${t} — تعلم من الصفر للاحتراف.` : `Course: ${t} — learn from zero to pro.`
  if (path.startsWith('/courses'))
    return locale === 'ar'
      ? 'تصفح كل الكورسات — من الصفر للاحتراف في AI والتصميم.'
      : 'Browse all courses — from zero to pro in AI and design.'
  if (path.startsWith('/zatoona'))
    return locale === 'ar' ? `مرجع بصري: ${t} — للاستخدام في برومبتات الصور والفيديو.` : `Visual reference: ${t} — for image and video prompts.`
  if (path.startsWith('/tutorials'))
    return locale === 'ar' ? `شرح ${t} — فيديو خطوة بخطوة.` : `Tutorial: ${t} — step-by-step video.`
  if (path.startsWith('/design-systems'))
    return locale === 'ar' ? `${t} — نظام تصميم DESIGN.md للذكاء الاصطناعي.` : `${t} — DESIGN.md system for AI coding.`
  if (path.startsWith('/ai-news/'))
    return locale === 'ar' ? `خبر: ${t} — ملخص بالعربي.` : `News: ${t} — summary.`
  return DEFAULT_DESCRIPTION[locale]
}

/**
 * Append (or replace) the `lang` query param. Used to build the
 * `hreflang ar` / `hreflang en` alternate URLs without needing path
 * rewrites. The `useLocaleSync` hook (below) reads this param on
 * navigation and switches the persisted locale to match.
 */
function addLangParam(href: string, lang: 'ar' | 'en'): string {
  try {
    const u = new URL(href)
    u.searchParams.set('lang', lang)
    return u.toString()
  } catch {
    // Bad URL — best-effort fallback. Don't crash SEO emission.
    return `${href}${href.includes('?') ? '&' : '?'}lang=${lang}`
  }
}

/**
 * Sets the document head for the current route. Updates <title>,
 * meta description, canonical, OG/Twitter cards, robots, and
 * (optional) JSON-LD. All tags this hook owns are tagged with
 * `data-managed="seo"` so the cleanup phase removes them precisely
 * without nuking the static head defined in `index.html`.
 */
export function Seo({
  title,
  description,
  path,
  image,
  type = 'website',
  noIndex,
  noSuffix,
  jsonLd,
  article,
}: SeoProps) {
  const { locale } = useLocale()

  useEffect(() => {
    const finalTitle = noSuffix ? title : `${title} — ${SUFFIX[locale]}`
    document.title = finalTitle

    // Canonical is locale-aware: Arabic (default) → bare path,
    // English → `?lang=en`. This matches the edge middleware so
    // hreflang alternates are consistent and crawlers don't see a
    // mismatched canonical.
    const baseUrl = path ? `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}` : SITE_URL
    // Canonical is always clean (no ?lang) — fixes duplicate URLs /?lang=en vs / 
    const url = baseUrl
    const desc = description ?? fallbackDescription(path, title, locale)
    // P0 International SEO: English URL with Arabic title = mismatch → لا تفهرس
    const hasArabicInTitle = /[\u0600-\u06FF]/.test(title)
    const mismatchNoIndex = locale === 'en' && hasArabicInTitle && !noSuffix
    const effectiveNoIndex = noIndex || mismatchNoIndex
    const img = image
      ? image.startsWith('http')
        ? image
        : `${SITE_URL}${image.startsWith('/') ? '' : '/'}${image}`
      : `${SITE_URL}${DEFAULT_OG_IMAGE}`

    const tags: Array<{ tag: 'meta'; attrs: Record<string, string> } | { tag: 'link'; attrs: Record<string, string> }> = [
      { tag: 'meta', attrs: { name: 'description', content: desc } },
      {
        tag: 'meta',
        attrs: {
          name: 'robots',
          content: effectiveNoIndex ? 'noindex,follow' : 'index,follow',
        },
      },
      // Open Graph
      { tag: 'meta', attrs: { property: 'og:type', content: type } },
      { tag: 'meta', attrs: { property: 'og:title', content: finalTitle } },
      { tag: 'meta', attrs: { property: 'og:description', content: desc } },
      { tag: 'meta', attrs: { property: 'og:url', content: url } },
      { tag: 'meta', attrs: { property: 'og:image', content: img } },
      {
        tag: 'meta',
        attrs: {
          property: 'og:locale',
          content: locale === 'ar' ? 'ar_AR' : 'en_US',
        },
      },
      // Twitter
      { tag: 'meta', attrs: { name: 'twitter:card', content: 'summary_large_image' } },
      { tag: 'meta', attrs: { name: 'twitter:site', content: '@abdallahissa' } },
      { tag: 'meta', attrs: { name: 'twitter:creator', content: '@abdallahissa' } },
      { tag: 'meta', attrs: { name: 'twitter:title', content: finalTitle } },
      { tag: 'meta', attrs: { name: 'twitter:description', content: desc } },
      { tag: 'meta', attrs: { name: 'twitter:image', content: img } },
      // Canonical + hreflang. We ship both `ar` and `en` alternates
      // pointing to query-param-scoped variants of the URL. This gives
      // Google two indexable variants per page (Audit Issue 3) without
      // requiring the full subdirectory router rewrite. When path-based
      // locale routing lands later, swap the `?lang=…` for `/ar/` /
      // `/en/` here and update the middleware accordingly.
      { tag: 'link', attrs: { rel: 'canonical', href: url } },
      {
        tag: 'link',
        attrs: {
          rel: 'alternate',
          hreflang: 'x-default',
          href: `${SITE_URL}${path ?? ''}`,
        },
      },
      {
        tag: 'link',
        attrs: {
          rel: 'alternate',
          hreflang: 'ar',
          href: addLangParam(`${SITE_URL}${path ?? ''}`, 'ar'),
        },
      },
      {
        tag: 'link',
        attrs: {
          rel: 'alternate',
          hreflang: 'en',
          href: addLangParam(`${SITE_URL}${path ?? ''}`, 'en'),
        },
      },
    ]

    // Article-specific OG tags (Facebook + LinkedIn render publish
    // date / author byline in their card preview from these).
    if (type === 'article' && article) {
      if (article.publishedTime) {
        tags.push({
          tag: 'meta',
          attrs: {
            property: 'article:published_time',
            content: article.publishedTime,
          },
        })
      }
      if (article.modifiedTime) {
        tags.push({
          tag: 'meta',
          attrs: {
            property: 'article:modified_time',
            content: article.modifiedTime,
          },
        })
      }
      if (article.author) {
        tags.push({
          tag: 'meta',
          attrs: { property: 'article:author', content: article.author },
        })
      }
      if (article.section) {
        tags.push({
          tag: 'meta',
          attrs: { property: 'article:section', content: article.section },
        })
      }
      if (article.tags) {
        for (const tag of article.tags) {
          tags.push({
            tag: 'meta',
            attrs: { property: 'article:tag', content: tag },
          })
        }
      }
    }

    const created: HTMLElement[] = []

    // Scrub SSR-injected SEO tags (data-ssr-seo="1") so we never end
    // up with both the edge-rendered and the React-managed tag in the
    // live DOM. The edge tags are critical for crawlers that don't
    // execute JS; once React mounts, our managed tags are the source
    // of truth.
    const ssrTags = document.head.querySelectorAll<HTMLElement>(
      '[data-ssr-seo="1"]',
    )
    ssrTags.forEach((el) => {
      // <title data-ssr-seo="1"> can't be removed without leaving the
      // page title-less for a frame; we simply rewrite it below via
      // `document.title`. Other tags are safe to drop here.
      if (el.tagName.toLowerCase() === 'title') return
      el.remove()
    })

    for (const { tag, attrs } of tags) {
      const el = document.createElement(tag)
      el.setAttribute('data-managed', 'seo')
      for (const [k, v] of Object.entries(attrs)) {
        el.setAttribute(k, v)
      }
      document.head.appendChild(el)
      created.push(el)
    }

    // JSON-LD
    let scriptEl: HTMLScriptElement | null = null
    if (jsonLd) {
      scriptEl = document.createElement('script')
      scriptEl.type = 'application/ld+json'
      scriptEl.setAttribute('data-managed', 'seo')
      scriptEl.text = JSON.stringify(jsonLd)
      document.head.appendChild(scriptEl)
      created.push(scriptEl)
    }

    return () => {
      for (const el of created) el.remove()
    }
  }, [title, description, path, image, type, noIndex, noSuffix, jsonLd, locale, article])

  return null
}

/**
 * Build a base `Person` JSON-LD for the site owner. Re-used across
 * the home page and the about page so the schema stays consistent.
 *
 * The `sameAs` list is the single source of truth for the site's
 * social profiles — Google uses it for the Knowledge Panel and to
 * cross-verify the author across the web.
 */
export function personSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Abdallah Issa',
    alternateName: 'عبدالله عيسى',
    url: SITE_URL,
    image: `${SITE_URL}/og-image.png`,
    jobTitle: 'AI Creative Educator',
    description:
      'مصمم ومطور ومتخصص في الذكاء الاصطناعي. علّمت أكتر من 1,500 طالب وعندي مجتمع 25,000+ متابع على السوشيال ميديا.',
    knowsAbout: [
      'Artificial Intelligence',
      'Prompt Engineering',
      'Generative AI',
      'Product Design',
      'Software Engineering',
      'AI for Creators',
    ],
    sameAs: [
      'https://www.linkedin.com/in/abdallahissa/',
      'https://www.tiktok.com/@abdallahissa120',
      'https://www.youtube.com/@abdallahissa',
      'https://www.instagram.com/abdallahissa',
      'https://x.com/abdallahissa',
      'https://abdallah-issa.com/about',
      'https://abdallah-issa.com/courses',
    ],
    hasOccupation: {
      '@type': 'Occupation',
      name: 'AI Creative Educator',
      occupationLocation: { '@type': 'Country', name: 'Egypt' },
    },
  }
}

/**
 * `WebSite` JSON-LD with sitelinks search action. Lets Google show
 * a search box under the site name in search results.
 */
export function websiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Abdallah Issa',
    alternateName: 'عبدالله عيسى',
    url: SITE_URL,
    inLanguage: ['ar', 'en'],
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/blog?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }
}

/**
 * `Organization` JSON-LD for the brand. Pairs with the `Person`
 * schema — Google treats them as related entities in the Knowledge
 * Graph.
 */
export function organizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Abdallah Issa',
    alternateName: 'عبدالله عيسى',
    url: SITE_URL,
    logo: `${SITE_URL}/icons/icon-512.png`,
    image: `${SITE_URL}/og-image.png`,
    description:
      'كورسات، أدوات، ومراجع بصرية في الذكاء الاصطناعي والتصميم والتطوير — للمبدعين العرب.',
    founder: {
      '@type': 'Person',
      name: 'Abdallah Issa',
      url: SITE_URL,
    },
    sameAs: [
      'https://www.linkedin.com/in/abdallahissa/',
      'https://www.tiktok.com/@abdallahissa120',
      'https://www.youtube.com/@abdallahissa',
      'https://www.instagram.com/abdallahissa',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'hello@abdallah-issa.com',
      availableLanguage: ['ar', 'en'],
      areaServed: ['EG', 'SA', 'AE', 'KW', 'QA', 'JO', 'MA'],
    },
  }
}

export const SEO_SITE_URL = SITE_URL
